import { EditIcon, TrashIcon } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { FormDetailPane } from '@/components/common/FormDetailPane';
import { TextFormField } from '@/components/common/FormField';
import { Button } from '@/components/ui/button';
import { FieldGroup } from '@/components/ui/field';
import type { Location } from '../data/databaseSchema';
import { handleApiError } from '../lib/errors';
import { locationService } from '../services/locationService';
import { logger } from '../utils/logger';

interface LocationFormData {
  name: string;
  office_head: string;
}

interface LocationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (location: Location) => void;
  onEntrySaved?: () => void;
  location?: Location | null;
  isEdit?: boolean;
  readOnly?: boolean;
  onEditRequest?: () => void;
  onDeleteRequest?: () => void;
}

export const LocationDrawer: React.FC<LocationDrawerProps> = ({
  isOpen,
  onClose,
  onSave,
  onEntrySaved,
  location,
  isEdit = false,
  readOnly = false,
  onEditRequest,
  onDeleteRequest,
}) => {
  const [formData, setFormData] = useState<LocationFormData>({
    name: '',
    office_head: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if ((isEdit || readOnly) && location) {
        setFormData({
          name: location.name || '',
          office_head: location.office_head || '',
        });
      } else {
        setFormData({ name: '', office_head: '' });
      }
      setErrors({});
      setSubmitError(null);
    }
  }, [isOpen, isEdit, readOnly, location]);

  const handleInputChange = (field: keyof LocationFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'Location name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const apiData = {
        name: formData.name.trim(),
        office_head: formData.office_head.trim() || undefined,
      };

      const response =
        isEdit && location
          ? await locationService.updateLocation(location.id, apiData)
          : await locationService.createLocation(apiData);

      onSave(response);
      onEntrySaved?.();
      onClose();
    } catch (error) {
      logger.error('Error saving location:', error);
      setSubmitError(handleApiError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldsDisabled = readOnly;

  return (
    <FormDetailPane
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={readOnly ? 'View Location' : isEdit ? 'Edit Location' : 'Add New Location'}
      description={
        readOnly
          ? 'Location details.'
          : isEdit
            ? 'Update the location details below.'
            : 'Fill in the details for the new location.'
      }
      onSubmit={readOnly ? undefined : handleSubmit}
      submitLabel={isEdit ? 'Update Location' : 'Add Location'}
      isSubmitting={isSubmitting}
      submitError={submitError}
      showFooter={!readOnly}
    >
      {readOnly ? (
        <div className="flex flex-wrap justify-end gap-2 border-b border-border-default pb-4">
          {onEditRequest ? (
            <Button type="button" size="sm" onClick={onEditRequest}>
              <EditIcon data-icon="inline-start" />
              Edit
            </Button>
          ) : null}
          {onDeleteRequest ? (
            <Button type="button" size="sm" variant="destructive" onClick={onDeleteRequest}>
              <TrashIcon data-icon="inline-start" />
              Delete
            </Button>
          ) : null}
        </div>
      ) : null}
      <FieldGroup>
        <TextFormField
          label="Location Name"
          required
          value={formData.name}
          onChange={(v) => handleInputChange('name', v)}
          error={errors.name}
          placeholder="Enter location name"
          disabled={fieldsDisabled}
        />
        <TextFormField
          label="Office Head"
          value={formData.office_head}
          onChange={(v) => handleInputChange('office_head', v)}
          placeholder="Enter office head name (optional)"
          disabled={fieldsDisabled}
        />
      </FieldGroup>
    </FormDetailPane>
  );
};
